import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { finalize } from 'rxjs';
import type { AdaSettings, SiteWindow } from '@core-mkt/interfaces/site-window';
import { MarketingSyncUserService } from '../sync-user/marketing-sync-user.service';
import { XGritApiService } from '../xgrit-api/xgrit-api.service';

const ADA_EMBED_POLL_INTERVAL_MS = 500;
const ADA_EMBED_MAX_POLL_TIME_MS = 60000;
const ADA_START_TIMEOUT_MS = 5000;

@Injectable({
  providedIn: 'root',
})
export class AdaTokenService {
  private adaScriptReady = false;
  private userData: any = null;
  private adaStarted = false;
  private adaStartedWithData = false; // Track if ADA was started with user data

  constructor(
    private readonly xps: XGritApiService,
    private readonly syncUserService: MarketingSyncUserService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  private waitForAdaScript(): Promise<NonNullable<SiteWindow['adaEmbed']>> {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();

      const poll = (): void => {
        const win = this.document.defaultView as unknown as SiteWindow | null;
        if (win?.adaEmbed) {
          this.adaScriptReady = true;
          resolve(win.adaEmbed);
          return;
        }

        if (Date.now() - startTime >= ADA_EMBED_MAX_POLL_TIME_MS) {
          reject(new Error('adaEmbed not available after 1 minute'));
          return;
        }

        setTimeout(poll, ADA_EMBED_POLL_INTERVAL_MS);
      };

      poll();
    });
  }

  private startAda(): void {
    if (this.adaStarted) {
      return; // Prevent double-start
    }

    const win = this.document.defaultView as unknown as SiteWindow | null;
    if (!win?.adaEmbed?.start) {
      // eslint-disable-next-line no-console
      console.warn('adaEmbed.start not available');
      return;
    }

    this.adaStarted = true;

    // Read the handle from the ADA script tag or window.adaSettings
    const adaScript = this.document.querySelector('script[id="__ada"]');
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const handle = adaScript?.getAttribute('data-handle') || win.adaSettings?.handle || undefined;

    // Build start options - must be AdaSettings type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const startOptions: AdaSettings = {
      handle: handle,
    };

    // Always start ADA immediately (without waiting for user data)
    // This allows interactive elements like toggle() to work right away
    if (this.userData) {
      // If we already have user data, start with it
      this.adaStartedWithData = true;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      startOptions.sensitiveMetaFields = this.userData;
    } else {
      // Start without user data - we'll update it later when API responds
      this.adaStartedWithData = false;
    }

    try {
      win.adaEmbed.start(startOptions);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error starting ADA:', error);
    }
  }

  private updateSensitiveFields(): void {
    const win = this.document.defaultView as unknown as SiteWindow | null;
    if (!win?.adaEmbed?.setSensitiveMetaFields) {
      // eslint-disable-next-line no-console
      console.warn('adaEmbed.setSensitiveMetaFields not available');
      return;
    }

    if (this.userData) {
      // Update ADA with user data after it's already started
      win.adaEmbed.setSensitiveMetaFields(this.userData);
    }
  }

  private tryStartAda(): void {
    if (this.adaScriptReady && !this.adaStarted) {
      // Both conditions met or timeout reached - start ADA
      this.startAda();
    }
  }

  private configureAdaSettings(): void {
    const win = this.document.defaultView as unknown as SiteWindow | null;
    if (!win) {
      return;
    }

    // Try to read the handle from the ADA script tag's data-handle attribute
    const adaScript = this.document.querySelector('script[id="__ada"]');
    const handle = adaScript?.getAttribute('data-handle') || undefined;

    // Initialize adaSettings on window object before ADA script loads
    // This configuration is read by the ADA embed script during initialization
    win.adaSettings = {
      handle: handle,
      // Other settings can be added here as needed
    };
  }

  syncUser(): void {
    // Set up timeout fallback - start ADA after 5s even without data
    const timeoutId = setTimeout(() => {
      if (!this.adaStarted) {
        // eslint-disable-next-line no-console
        console.warn('Starting ADA without user data due to timeout');
        this.tryStartAda();
      }
    }, ADA_START_TIMEOUT_MS);

    this.syncUserService.syncUserReady$.subscribe({
      next: (response) => {
        clearTimeout(timeoutId);

        if (response.success && response.data?.adaToken) {
          // Store user data
          this.userData = response.data;

          // Try to start ADA now that we have data
          this.tryStartAda();

          // If ADA was already started without data, update the sensitive fields now
          if (this.adaStarted && !this.adaStartedWithData) {
            this.updateSensitiveFields();
          }
        } else {
          // No valid data, start ADA without sensitive fields
          this.tryStartAda();
        }
      },
      error: () => {
        clearTimeout(timeoutId);
        // eslint-disable-next-line no-console
        console.warn('Failed to get ada token');

        // Start ADA without user data on API failure
        this.tryStartAda();
      },
    });
  }

  setAdaToken(): void {
    // Configure window.adaSettings before ADA script loads
    // This is required for the ADA embed script to initialize properly
    this.configureAdaSettings();

    // Start waiting for ADA script to load
    this.waitForAdaScript()
      .then(() => {
        // Script is ready, try to start if we have data
        this.tryStartAda();
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.warn('adaEmbed script not available:', error);
        // Can't start ADA if script never loads
      });

    // Check for transferToken query parameter
    const urlParams = new URLSearchParams(this.document.defaultView?.location.search);
    const transferToken = urlParams.get('transferToken');
    if (transferToken) {
      // IF token is expired or not valid still try to sync user in case cookie already exists.
      this.xps
        .getUserByTransferToken(transferToken)
        .pipe(finalize(() => this.syncUser()))
        .subscribe();
    } else {
      this.syncUser();
    }
  }
}
